import React, { useState } from "react";
import { Settings2, CreditCard, TrendingDown, XCircle, RotateCcw, Users } from "lucide-react";
import Button from "../ui/Button";

/**
 * Secondary/dangerous billing actions that don't belong on the main Plan card.
 * Rendered as its own section below Usage Alerts.
 */
export default function ManageBillingCard({
  subscription: s,
  summary,
  isPayg = false,
  onDowngrade,
  onCancel,
  onReactivate,
  onManageCards,
}) {
  const [confirmCancel, setConfirmCancel] = useState(false);

  if (!s) return null;

  // Shared-billing disclosure (3+ workspaces under one billing account) lives
  // here in Manage billing rather than as a page-top banner.
  const workspaces = summary?.workspaces || [];
  const billingOwner = summary?.billing_account?.owner_email;
  const showShared = workspaces.length > 2 && !!billingOwner;

  const isCanceledOrPending = s.status === "canceled" || s.cancel_at_period_end;
  const isEnterprise = s.plan && s.plan.is_public === false;
  // PAYG is already the floor — there's nothing to downgrade to.
  // The `code !== "starter"` exclusion used to be right: Starter WAS the price floor,
  // so there was nothing below it to move to. Pay-as-you-go is the floor now, and
  // DowngradeModal already refuses anything that is not genuinely cheaper — so keeping
  // the exclusion only stranded the grandfathered Starter customers this whole
  // migration exists to move, forcing them to contact support to reach $0.
  const canDowngrade = !isPayg && !isCanceledOrPending && !isEnterprise &&
                        s.plan?.is_public !== false;

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Settings2 className="h-4 w-4 text-muted-foreground" />
          Manage billing
        </div>
      </div>
      <div className="p-4 space-y-3">
        {/* Shared billing disclosure */}
        {showShared && (
          <div className="flex items-start gap-3 rounded-lg bg-muted/40 border border-border p-3">
            <div className="w-9 h-9 rounded-lg bg-card border border-border flex items-center justify-center flex-shrink-0">
              <Users className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-foreground">Shared billing</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                Owned by {billingOwner}. Covers {workspaces.length} workspaces:{" "}
                {workspaces.map(w => w.name).join(", ")}.
              </div>
            </div>
          </div>
        )}

        {/* Payment methods */}
        <ActionRow
          icon={CreditCard}
          title="Payment methods"
          description="Add, remove, or update saved cards and view your invoices in Stripe's secure portal."
          actionLabel="Manage"
          onClick={onManageCards}
        />

        {/* Downgrade */}
        {canDowngrade && (
          <ActionRow
            icon={TrendingDown}
            title="Downgrade plan"
            description="Keep your current plan until period end, then switch to a lower tier."
            actionLabel="Downgrade"
            onClick={onDowngrade}
          />
        )}

        {/* Cancel (when active) */}
        {!isPayg && !isCanceledOrPending && !isEnterprise && (
          <ActionRow
            icon={XCircle}
            title="Cancel subscription"
            description="Keep access until period end. You can reactivate anytime before then."
            actionLabel={confirmCancel ? "Confirm cancel" : "Cancel"}
            destructive
            onClick={() => {
              if (confirmCancel) { onCancel(); setConfirmCancel(false); }
              else { setConfirmCancel(true); }
            }}
            cancel={confirmCancel ? () => setConfirmCancel(false) : null}
          />
        )}

        {/* Reactivate (when canceled / pending cancel) */}
        {isCanceledOrPending && (
          <ActionRow
            icon={RotateCcw}
            title={s.cancel_at_period_end ? "Cancel pending cancellation" : "Reactivate subscription"}
            description={
              s.cancel_at_period_end
                ? "You scheduled a cancellation. Click to keep your subscription active."
                : "Your subscription is canceled. Click to re-enable it."
            }
            actionLabel={s.cancel_at_period_end ? "Undo cancel" : "Reactivate"}
            onClick={onReactivate}
          />
        )}

        {isEnterprise && (
          <p className="text-sm text-muted-foreground">
            You're on a custom Enterprise plan. Contact your account manager to manage changes.
          </p>
        )}
      </div>
    </div>
  );
}

function ActionRow({ icon: Icon, title, description, actionLabel, onClick, destructive, cancel }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="w-9 h-9 rounded-lg bg-muted/50 border border-border flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        <div className="text-xs text-muted-foreground line-clamp-1">{description}</div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {cancel && (
          <button onClick={cancel} className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md">
            Keep
          </button>
        )}
        <Button variant={destructive ? "danger" : "secondary"} onClick={onClick}>
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}
